'use client';

import { useTranslations } from 'next-intl';
import { useRef, useState, useEffect, useCallback } from 'react';

import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import CloseIcon from '@/fo/icons/base/icons/close16.svg';
import { UiKitCheckboxBase } from '@/fo/ui/kit/checkbox/base';

import { styles } from './UiDoctorFilterBase.styles';

export interface DoctorFilterItem {
  id: string;
  label: string;
}

export interface UiDoctorFilterBaseProps {
  items: DoctorFilterItem[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  minSelected?: number;
}

export function UiDoctorFilterBase({
  items,
  selected,
  onChange,
  placeholder,
  className,
  minSelected = 0
}: UiDoctorFilterBaseProps) {
  const disableClose = minSelected > 0 && selected.length <= minSelected;
  const slots = styles({ disableClose });
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handler = (event: Event) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);

    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const onOptionChange = useCallback(
    (id: string) => {
      const newSelected = selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id];

      onChange(newSelected);
    },
    [selected, onChange]
  );

  const onTagRemove = useCallback(
    (id: string) => {
      const newSelected = selected.filter((item) => item !== id);
      onChange(newSelected);
    },
    [selected, onChange]
  );

  const onToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const selectedItems = items.filter((item) => selected.includes(item.id));

  return (
    <div
      className={slots.container({ className })}
      ref={ref}
    >
      <div
        className={slots.trigger()}
        onClick={onToggle}
      >
        <div className={slots.value()}>
          {selectedItems.length > 0 && (
            <div className={slots.tags()}>
              {selectedItems.map((item) => (
                <div
                  className={slots.tag()}
                  key={item.id}
                >
                  <div
                    onClick={(e) => {
                      if (disableClose) return;

                      e.stopPropagation();
                      onTagRemove(item.id);
                    }}
                  >
                    <CloseIcon className={slots.closeIcon()} />
                  </div>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          )}
          {selected.length === 0 && (
            <span className={slots.placeholder()}>
              {placeholder ?? t('It bothers me')}
            </span>
          )}
        </div>
        <span className={slots.iconArrow({ open })}>
          <ChevronIcon />
        </span>
      </div>
      {open && (
        <div className={slots.dropdown()}>
          {items.map((item) => (
            <div
              className={slots.optionRow()}
              key={item.id}
            >
              <UiKitCheckboxBase
                checked={selected.includes(item.id)}
                label={item.label}
                onChange={() => onOptionChange(item.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
